/**
 * Cable lengths that fit in the box they are printed in.
 *
 * The same problem `formatMass` solves for weight. A single drum is 2,000 m and
 * a year of orders is 1,400,000 m; printed the same way, one of them destroys
 * the card it sits in and neither reads at a glance.
 *
 * The metre is the working unit on the floor — a run is planned, cut and
 * labelled in metres, and rounding one to "2.0 km" would be a step backwards.
 * So the switch happens late, at ten kilometres, where the number has stopped
 * being an amount somebody handles and become an amount somebody reports.
 *
 * `exactLength` keeps the metre for a title attribute, so the summary figure
 * never hides the real one.
 */

const KM = 1000;
const SWITCH = 10_000;

const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** "2,000 m", "18.5 km", "1,412 km" — whichever reads best at that size. */
export function formatLength(m, { unit = true } = {}) {
  const v = num(m);
  const abs = Math.abs(v);

  if (abs < SWITCH) {
    return `${Math.round(v).toLocaleString()}${unit ? ' m' : ''}`;
  }

  const km = v / KM;
  const akm = Math.abs(km);
  const dp = akm >= 1000 ? 0 : akm >= 100 ? 1 : 2;
  return `${km.toLocaleString(undefined, {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  })}${unit ? ' km' : ''}`;
}

/** The full figure, for a tooltip. */
export function exactLength(m) {
  return `${Math.round(num(m)).toLocaleString()} m`;
}

/** Just the unit, when a layout needs the number and the unit apart. */
export function lengthUnit(m) {
  return Math.abs(num(m)) < SWITCH ? 'm' : 'km';
}

/** Kilometres as a bare number, for chart axes that carry the unit themselves. */
export function toKm(m) {
  return Math.round((num(m) / KM) * 100) / 100;
}
